<script setup>
import { ref, watch } from 'vue'
import { api } from '../api/client'
import { formatDateTime } from '../utils'
import { useToast } from '../composables/useToast'
import ModalDialog from './ModalDialog.vue'

const props = defineProps({
  open: Boolean,
  resource: { type: Object, default: null },
})
const emit = defineEmits(['close'])
const { show } = useToast()

const loading = ref(false)
const loaded = ref(false)
const saving = ref(false)
const error = ref('')
const exportUrl = ref('')
const importUrl = ref('')
const lastSyncedAt = ref(null)
const lastError = ref(null)
const lastWarning = ref(null)
const exportInput = ref(null)
let loadSequence = 0

function applySettings(settings) {
  exportUrl.value = settings?.export_url || ''
  importUrl.value = settings?.import_url || ''
  lastSyncedAt.value = settings?.last_synced_at || null
  lastError.value = settings?.last_error || null
  lastWarning.value = settings?.last_warning || null
}

async function loadSettings(resourceId) {
  const sequence = ++loadSequence
  error.value = ''
  loaded.value = false
  applySettings(null)
  loading.value = true
  try {
    const settings = await api.resources.getIcal(resourceId)
    if (sequence === loadSequence) {
      applySettings(settings)
      loaded.value = true
    }
  } catch (requestError) {
    if (sequence === loadSequence) error.value = requestError.message || 'Не удалось загрузить настройки календаря.'
  } finally {
    if (sequence === loadSequence) loading.value = false
  }
}

watch(() => [props.open, props.resource?.id], ([open, resourceId]) => {
  if (open && resourceId) loadSettings(resourceId)
  else ++loadSequence
})

async function copyExportUrl() {
  if (!exportUrl.value) return
  try {
    await navigator.clipboard.writeText(exportUrl.value)
    show('Ссылка календаря CRM скопирована.')
  } catch {
    exportInput.value?.focus()
    exportInput.value?.select()
    show('Скопируйте выделенную ссылку вручную.', 'error')
  }
}

async function save() {
  if (!props.resource?.id || !loaded.value || loading.value || saving.value) return
  saving.value = true
  error.value = ''
  try {
    const settings = await api.resources.updateIcal(props.resource.id, {
      import_url: importUrl.value.trim() || null,
    })
    applySettings(settings)
    show(importUrl.value ? 'Календарь Авито подключён. Занятость появится после синхронизации.' : 'Импорт календаря Авито отключён.')
  } catch (requestError) {
    error.value = requestError.message || 'Не удалось сохранить календарь Авито.'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <ModalDialog
    :open="open"
    :title="`Календарь Авито · ${resource?.name || 'Дом'}`"
    eyebrow="Синхронизация занятости"
    @close="!saving && emit('close')"
  >
    <div class="ical-dialog">
      <p class="ical-dialog__intro">Для каждого дома нужен отдельный календарь. Даты обновляются автоматически, но не мгновенно: проверяйте новые брони перед подтверждением.</p>

      <p v-if="loading" class="ical-dialog__state" role="status">Загружаем настройки календаря…</p>

      <div v-else-if="!loaded" class="ical-dialog__load-error">
        <p class="form-error" role="alert">{{ error || 'Не удалось загрузить настройки календаря.' }}</p>
        <button type="button" class="button button--secondary" @click="loadSettings(resource.id)">Повторить</button>
      </div>

      <template v-else>
        <section class="ical-dialog__section" aria-labelledby="ical-export-heading">
          <h3 id="ical-export-heading">Из CRM в Авито</h3>
          <p>Добавьте эту ссылку в Авито как внешний календарь. Она содержит приватный ключ — не публикуйте её.</p>
          <div class="ical-dialog__copy-row">
            <label class="field">
              <span>Ссылка календаря CRM</span>
              <input ref="exportInput" :value="exportUrl" type="text" readonly aria-label="Ссылка календаря CRM" @focus="$event.target.select()" />
            </label>
            <button type="button" class="button button--secondary" :disabled="!exportUrl" @click="copyExportUrl">Копировать</button>
          </div>
        </section>

        <form class="ical-dialog__section" @submit.prevent="save">
          <h3>Из Авито в CRM</h3>
          <p>Вставьте ссылку экспорта календаря из объявления Авито. Занятые даты появятся в шахматке как блокировки без данных гостя и цены.</p>
          <label class="field">
            <span>Ссылка на календарь Авито</span>
            <input v-model="importUrl" type="url" inputmode="url" autocomplete="off" placeholder="https://…/calendar.ics" />
          </label>
          <p class="ical-dialog__hint">Чтобы отключить импорт, очистите поле и сохраните. Ссылка CRM останется рабочей.</p>
          <p v-if="lastSyncedAt" class="ical-dialog__status">Последняя синхронизация: {{ formatDateTime(lastSyncedAt) }}</p>
          <p v-else-if="importUrl" class="ical-dialog__status">Синхронизации пока не было.</p>
          <p v-if="lastWarning" class="ical-dialog__warning" role="status">Возможное пересечение с бронью CRM: {{ lastWarning }}</p>
          <p v-if="lastError" class="form-error" role="alert">Последняя ошибка синхронизации: {{ lastError }}</p>
          <p v-if="error" class="form-error" role="alert">{{ error }}</p>
          <footer class="modal-actions">
            <button type="button" class="button button--ghost" :disabled="saving" @click="emit('close')">Закрыть</button>
            <button type="submit" class="button button--primary" :disabled="saving || !loaded">
              <span v-if="saving" class="button-spinner" aria-hidden="true" />
              {{ saving ? 'Сохраняем…' : 'Сохранить' }}
            </button>
          </footer>
        </form>
      </template>
    </div>
  </ModalDialog>
</template>
